import React from 'react';
import { Link } from 'react-router-dom';
import {
    Anchor,
    Shield,
    Radio,
    Radar,
    Server,
    Wifi,
    ArrowRight,
    CheckCircle2,
    Navigation,
    Globe,
    Zap,
    Clock
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernNavySolutions = () => {
    const products = [
        {
            icon: <Radio size={28} />,
            title: 'Shipboard Communication Systems',
            description: 'Advanced maritime communication networks with satellite connectivity.',
            badge: 'Fleet-ready',
            features: ['SATCOM integration', 'Secure voice/data', 'Shore-to-ship', 'Inter-fleet comms']
        },
        {
            icon: <Radar size={28} />,
            title: 'Naval Combat Systems',
            description: 'Integrated combat management and situational awareness platforms.',
            badge: 'Combat-proven',
            features: ['Radar integration', 'Threat detection', 'Fire control', 'C4I systems']
        },
        {
            icon: <Server size={28} />,
            title: 'Maritime Data Centers',
            description: 'Shipboard computing infrastructure designed for naval operations.',
            badge: 'MIL-SPEC',
            features: ['Shock-mounted', 'Salt-air resistant', 'Redundant power', 'High availability']
        },
        {
            icon: <Shield size={28} />,
            title: 'Cybersecurity for Fleets',
            description: 'Comprehensive security solutions for naval networks and systems.',
            badge: 'DoD Certified',
            features: ['Network defense', 'Threat monitoring', 'Incident response', 'Compliance']
        }
    ];

    const capabilities = [
        {
            icon: <Navigation size={28} />,
            metric: '24/7',
            title: 'Operational Support',
            description: 'Round-the-clock support for fleet operations'
        },
        {
            icon: <Globe size={28} />,
            metric: 'Global',
            title: 'Worldwide Coverage',
            description: 'Support wherever your vessels operate'
        },
        {
            icon: <Zap size={28} />,
            metric: '<2hrs',
            title: 'Rapid Response',
            description: 'Quick deployment of critical updates'
        },
        {
            icon: <Clock size={28} />,
            metric: '99.9%',
            title: 'System Uptime',
            description: 'Reliable systems for mission success'
        }
    ];

    const features = [
        { icon: <Anchor size={24} />, title: 'Maritime', text: 'Naval-specific solutions' },
        { icon: <Shield size={24} />, title: 'Secure', text: 'Military encryption' },
        { icon: <Wifi size={24} />, title: 'Connected', text: 'Global connectivity' },
        { icon: <Radar size={24} />, title: 'Aware', text: 'Situational awareness' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Navy Solutions"
                subtitle="Maritime excellence through technology"
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'Department of Defense', path: '/department' },
                    { label: 'Navy Solutions' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid jk-military-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Anchor size={16} />
                                U.S. Navy Technology Partner
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Advanced Maritime Technology for
                                <span className="highlight"> Naval Operations</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We provide cutting-edge technology solutions specifically designed for naval
                                operations. From shipboard communications to combat systems, our solutions
                                enable maritime excellence and mission success.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Deploy Solutions
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/department" className="jk-btn jk-btn--secondary">
                                    DoD Overview
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Server size={16} />
                            Maritime Solutions
                        </span>
                        <h2 className="jk-solutions-services__title">
                            Fleet-Ready
                            <span className="highlight"> Technology</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            Systems designed for the unique challenges of naval operations
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                        {products.map((product, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-header">
                                    <div className="jk-solutions-services__card-icon">
                                        {product.icon}
                                    </div>
                                    <span className="jk-solutions-services__card-badge">{product.badge}</span>
                                </div>
                                <h3 className="jk-solutions-services__card-title">{product.title}</h3>
                                <p className="jk-solutions-services__card-description">{product.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {product.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="jk-solutions-capabilities">
                <div className="jk-solutions-capabilities__container">
                    <div className="jk-solutions-capabilities__header">
                        <h2 className="jk-solutions-capabilities__title">
                            Operational
                            <span className="highlight"> Capabilities</span>
                        </h2>
                    </div>

                    <div className="jk-solutions-capabilities__grid">
                        {capabilities.map((capability, index) => (
                            <div key={index} className="jk-solutions-capabilities__card">
                                <div className="jk-solutions-capabilities__card-icon">
                                    {capability.icon}
                                </div>
                                <div className="jk-solutions-capabilities__card-metric">{capability.metric}</div>
                                <h3 className="jk-solutions-capabilities__card-title">{capability.title}</h3>
                                <p className="jk-solutions-capabilities__card-text">{capability.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernNavySolutions;
